import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import PersonIcon from '@mui/icons-material/Person'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import BlockIcon from '@mui/icons-material/Block'

/**
 * CheckinRequestModal Component
 * 
 * Displays a popup when a mentor requests access to a room.
 * 
 * Props:
 * - open: boolean - Whether the modal is open
 * - mentorName: string - Name of the mentor requesting access
 * - reason: string - Reason for the checkin request
 * - onCheckout: function - Callback when checkout button is clicked
 * - onIgnore: function - Callback when ignore button is clicked
 * - onClose: function - Callback to close the modal
 * - loading: boolean - Whether an action is in progress
 * 
 * Behavior:
 * - Shows mentor name and reason for request
 * - Provides "Checkout" and "Ignore" buttons
 * - Can be closed via X button (calls onClose)
 */
export default function CheckinRequestModal({
  open,
  mentorName,
  reason,
  onCheckout,
  onIgnore,
  onClose,
  loading = false,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6">Check-in Request</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={loading}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Mentor Information */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Mentor Requesting Access
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {mentorName || 'Unknown Mentor'}
              </Typography>
            </Box>
          </Box>

          {/* Reason */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Reason:
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {reason || 'No reason provided'}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
            What would you like to do?
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={onIgnore}
          variant="outlined"
          color="inherit"
          startIcon={<BlockIcon />}
          disabled={loading}
          sx={{ flex: 1 }}
        >
          Ignore
        </Button>
        <Button
          onClick={onCheckout}
          variant="contained"
          color="primary"
          startIcon={<ExitToAppIcon />}
          disabled={loading}
          sx={{ flex: 1 }}
        >
          {loading ? 'Processing...' : 'Checkout'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}










